import { createSlice } from "@reduxjs/toolkit";

const FEATURE_KEYS = [
  "accountSummary",
  "pendingApprovals",
  "transactionHistorySuyool",
  "transactionHistoryBsl",
  "corporateCards",
  "accountDetails",
  "payCorporations",
  "suyoolToSuyool",
  "payroll",
  "payrollOverview",
  "payrollSummary",
  "quickPay",
  "exchange",
  "bills",
  "internationalTransfers",
  "bankTransfer",
  "cardTransactions",
  "cashDeposit",
  "cashWithdraw",
  "bslAccountOverview",
  "bslToSuyool",
  "employees",
  "merchantSidebar",
  "branches",
  "paymentRequest",
  "externalCashDeposit",
  "butlerService",
  "eodClosing",
  "merchantInformation",
  "merchantEnrollment",
  "salesOverview",
  "salesTransactions",
  "transferAbroad",
  "vat",
  "taxes",
  "aiAssistantDashboard",
  "makePayment",
];

const createTableBuckets = (keys) =>
  keys.reduce((accumulator, key) => {
    accumulator[key] = {
      data: [],
      pagination: null,
      loading: false,
    };
    return accumulator;
  }, {});

const createFeatureBuckets = (keys) =>
  keys.reduce((accumulator, key) => {
    accumulator[key] = {};
    return accumulator;
  }, {});

const isMergeableObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const initialState = {
  loading: false,
  error: null,
  websocket: {
    encryptedUrl: "",
    connected: false,
    lastEvent: null,
    events: [],
  },
  tables: createTableBuckets(FEATURE_KEYS),
  forms: createFeatureBuckets(FEATURE_KEYS),
  summary: createFeatureBuckets(FEATURE_KEYS),
  details: createFeatureBuckets(FEATURE_KEYS),
  ui: createFeatureBuckets(FEATURE_KEYS),
  merchant: {
    sidebar: [],
    permissions: [],
    features: [],
  },
  settingObjectData: {},
  
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    settingData: (state, action) => {
      const { field, value } = action.payload;
      state[field] = value;
    },

   
    setFeatureData: (state, action) => {
      const { section, key, value } = action.payload || {};
      if (!section || !key) return;

      if (!state[section] || typeof state[section] !== "object") {
        state[section] = {};
      }

      const previousValue = state[section][key];
      if (isMergeableObject(previousValue) && isMergeableObject(value)) {
        state[section][key] = {
          ...previousValue,
          ...value,
        };
        return;
      }

      state[section][key] = value;
    },
    setSelectedStatementCard: (state, action) => {
      state.details.cardTransactions = {
        ...(state.details.cardTransactions || {}),
        selectedStatementCard: action.payload || null,
        isFromViewStatement: Boolean(action.payload),
      };
    },
    clearSelectedStatementCard: (state) => {
      state.details.cardTransactions = {
        ...(state.details.cardTransactions || {}),
        selectedStatementCard: null,
        isFromViewStatement: false,
      };
    },
    setTableData: (state, action) => {
      const { key, data, pagination } = action.payload || {};
      if (!key) return;

      const previous = state.tables[key] || {};
      state.tables[key] = {
        ...previous,
        data: data ?? previous.data ?? [],
        pagination: pagination ?? previous.pagination ?? null,
      };
    },
    setTableLoading: (state, action) => {
      const { key, loading } = action.payload || {};
      if (!key) return;

      const previous = state.tables[key] || {};
      state.tables[key] = {
        ...previous,
        loading: Boolean(loading),
      };
    },
    setLoading: (state, action) => {
      state.loading = Boolean(action.payload);
    },
    setError: (state, action) => {
      state.error = action.payload || null;
    },
    setWebSocketToken: (state, action) => {
      state.websocket.encryptedUrl = action.payload || "";
    },
    setWebSocketConnected: (state, action) => {
      state.websocket.connected = Boolean(action.payload);
    },
    addWebSocketEvent: (state, action) => {
      state.websocket.lastEvent = action.payload || null;
      if (action.payload) {
        state.websocket.events.push(action.payload);
      }
    },
    clearWebSocketState: (state) => {
      state.websocket = { ...initialState.websocket };
      state.details.corporateCards = {
        ...(state.details.corporateCards || {}),
        activeRevealedCardId: null,
        cardDetailDecryptKey: null,
        cardDetailEncryptedPayloads: null,
        cardDetailSocketPayload: null,
        cardDetailError: null,
        createdDisposableCard: null,
        hasReceivedVirtualCardQrAuthorization: false,
        isAwaitingCardDetails: false,
        isFetchingCardDetails: false,
        isWaitingForVirtualCardQrAuthorization: false,
        pendingCardDetailRequest: false,
        pendingVirtualCardRequestPayload: null,
        revealExpiresAt: null,
        revealedCards: {},
        selectedCardIdForReveal: null,
      };
    },
    clearField: (state, action) => {
      const field = action.payload;
      if (!field || !(field in state)) return;
      state[field] = initialState[field];
    },
  },
});

export const {
  settingData,
  setFeatureData,
  setSelectedStatementCard,
  clearSelectedStatementCard,
  setTableData,
  setTableLoading,
  setLoading,
  setError,
  setWebSocketToken,
  setWebSocketConnected,
  addWebSocketEvent,
  clearWebSocketState,
  clearField,
} = appSlice.actions;

export default appSlice.reducer;
